/**
 * Preview copied stats config before opening Edit sent with pre-filled fields.
 */

import type { FC } from 'react';
import { ClipboardPaste, X } from 'lucide-react';

import { Button } from '@/components/ui';
import type { BulkUpdatePreparedDropsPayload } from '@/hooks';

import { buildApplyCopiedPreviewSummary } from './apply-copied';
import { backdropClick } from '@/components/ui';

export interface ApplyCopiedConfigSentPreviewModalProps {
  open: boolean;
  payload: BulkUpdatePreparedDropsPayload | null;
  workspaceWiserId: number;
  campaigns: Array<{ id: number; name: string }>;
  gateways: Array<{ id: number; name: string }>;
  selectedDropCount: number;
  onClose: () => void;
  onConfirmEdit: () => void;
}

export const ApplyCopiedConfigSentPreviewModal: FC<ApplyCopiedConfigSentPreviewModalProps> = ({
  open,
  payload,
  workspaceWiserId,
  campaigns,
  gateways,
  selectedDropCount,
  onClose,
  onConfirmEdit,
}) => {
  if (!open) return null;

  const summary = payload
    ? buildApplyCopiedPreviewSummary(payload, workspaceWiserId, campaigns, gateways)
    : { lines: ['Nothing copied.'], canConfirm: false, mismatchMessage: null as string | null };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full min-w-[280px] max-w-md flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-copied-sent-title"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 id="apply-copied-sent-title" className="flex items-center gap-2 text-base font-semibold">
            <ClipboardPaste className="h-4 w-4 shrink-0" />
            Apply copied config
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-muted-foreground">
            Review what will be pre-filled for {selectedDropCount} selected drop
            {selectedDropCount !== 1 ? 's' : ''}. Save in the editor to update SMSWiser.
          </p>
          <ul className="list-inside list-disc space-y-1.5 text-sm text-foreground">
            {summary.lines.map((line, i) => (
              <li key={`${i}-${line.slice(0, 48)}`}>{line}</li>
            ))}
          </ul>
          {summary.mismatchMessage ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
              {summary.mismatchMessage}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={onConfirmEdit} disabled={!summary.canConfirm}>
            Open in editor
          </Button>
        </div>
      </div>
    </div>
  );
};
